import MaskDrawingTool from "$/components/MaskDrawingTool";
import { useAuth } from "$/util/auth";
import {
  Box,
  Button,
  Grid,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CreateDesignTool from "./CreateDesignTool";

const typeMap = {
  coverup: "AI Cover Up",
  filler: "Filler",
};

const typePromptMap = {
  coverup: "Coverup",
  filler: "Filler",
};

const descriptionMap = {
  coverup:
    "Generate custom coverup designs that effectively conceal unwanted tattoos or scars with AI.",
  filler:
    "Create custom filler tattoos to seamlessly fill gaps and enhance existing ink with AI.",
};

const themeMap = {
  floral: "Floral",
  space: "Space",
  norse: "Norse Mythology",
  clouds: "Clouds and Smoke",
  flames: "Flames",
  spiderwebs: "Spider Webs",
  stars: "Stars and Dots",
  waves: "Wave Patterns",
  lightning: "Lightning Bolts",
  abstract: "Abstract Patterns",
  mandalas: "Mini Mandalas",
  animals: "Small Animals",
};

function MaskDrawingGuide(props) {
  const [img, setImg] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [maskTheme, setMaskTheme] = useState(null);
  const auth = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const midScreen = useMediaQuery(theme.breakpoints.only("md"));
  const isPro = (auth.user && auth.user.isActiveSubscription) || props.showAll;

  const handleSave = (mask, img) => {
    setImg(img);
    setActiveStep(2);
    navigate(".", {
      state: {
        prompt: `${typePromptMap[props.type]} ${themeMap[maskTheme]}`,
        customShapeImg: img.src,
        customShapeMask: mask,
        bodyPart: "square",
        autorun: true,
      },
    });
  };

  useEffect(() => {
    setActiveStep(0);
  }, [location.pathname]);

  if (activeStep === 2) {
    return (
      <CreateDesignTool
        onBack={() => {
          setActiveStep(0);
        }}
      />
    );
  }

  return (
    <>
      <Typography variant="h4" gutterBottom mt={1} mb={2}>
        {typeMap[props.type] || "AI"} Tattoo Generator
      </Typography>
      <Stack mb={4} direction={fullScreen ? "column-reverse" : "row"} spacing={4}>
        <Box>
          <Typography variant="body1" gutterBottom pb={1}>
            {descriptionMap[props.type] || "Draw a silhouette of the design you want."}
          </Typography>

          <ol style={{ paddingLeft: "20px" }}>
            <li>Select a theme for the design</li>
            <li>Draw a silhouette of the region you want to cover up or fill</li>
            <li>
              Hit continue and you can start generating AI designs based on your theme
              and drawing
            </li>
          </ol>
          <img
            src={"/images/filler-coverup-steps.png"}
            width="100%"
            style={{
              borderRadius: "5px",
              // Center
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              margin: "0 0 10px 10px",
            }}
            alt={`${typeMap[props.type] || "AI"} Tattoo Generator`}
          />
          <Typography variant="body1" gutterBottom pb={1}>
            Black regions act as a flexible guide, influencing design placement based on
            your tattoo idea, style, and advanced settings.
          </Typography>
        </Box>
        <Box>
          <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
            <Step key={0}>
              <StepButton
                color="inherit"
                onClick={() => setActiveStep(0)}
                optional={maskTheme && <>{themeMap[maskTheme]}</>}
              >
                Select a Theme
              </StepButton>
            </Step>
            <Step key={1}>
              <StepButton color="inherit" onClick={() => setActiveStep(1)}>
                Draw Shape
              </StepButton>
            </Step>
            <Step key={2}>
              <StepButton color="inherit" onClick={() => setActiveStep(2)}>
                Generate Designs
              </StepButton>
            </Step>
          </Stepper>
          {activeStep === 0 && (
            // Theme Buttons
            <Grid container spacing={2}>
              {Object.keys(themeMap).map((theme) => (
                <Grid item xs={6} key={theme}>
                  <Button
                    size="large"
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    onClick={() => {
                      setMaskTheme(theme);
                      setActiveStep(1);
                    }}
                  >
                    {themeMap[theme]}
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}
          {activeStep === 1 && (
            <Box maxWidth={400} mx="auto">
              <MaskDrawingTool
                onMaskChange={handleSave}
                maskImg={img}
                actionText="Continue"
              />
            </Box>
          )}
        </Box>
      </Stack>
    </>
  );
}

export default MaskDrawingGuide;
